import React, { useEffect, useRef, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { Button } from '@actual-app/components/button';
import { Text } from '@actual-app/components/text';
import { View } from '@actual-app/components/view';

import { cartao, cor, espaco, texto } from '#components/finapp/estilo';
import type {
  MapaDeTipos,
  TipoDeGasto,
} from '#components/finapp/motor/tiposDeGasto';
import {
  CHAVE_PREF,
  chutarTipo,
  gravarMapa,
  lerMapa,
  ROTULO_SEM_TIPO,
} from '#components/finapp/motor/tiposDeGasto';
import { useCategories } from '#hooks/useCategories';
import { useSyncedPref } from '#hooks/useSyncedPref';

const TIPOS: TipoDeGasto[] = ['essencial', 'luxo', 'investimento'];

const ROTULOS: Record<TipoDeGasto, string> = {
  essencial: 'Essencial',
  luxo: 'Luxo',
  investimento: 'Investimento',
};

/**
 * ⚠️⚠️ Revisão final da branch (Minor) — o cabeçalho ("N categorias sem
 * tipo") usava dois literais PRÓPRIOS que coincidiam com `ROTULO_SEM_TIPO`
 * por acaso; se alguém renomeasse o balde lá (`motor/tiposDeGasto.ts`),
 * este arquivo divergeria em silêncio. `ROTULO_SEM_TIPO` ("Sem tipo") é o
 * rótulo CANÔNICO de um balde, usado em início de frase ("Sem tipo" como
 * título) — em minúscula aqui porque entra no MEIO da frase ("1 categoria
 * sem tipo").
 */
const rotuloSemTipoMinusculo = ROTULO_SEM_TIPO.toLowerCase();

/**
 * Onde o dono corrige o chute do app.
 *
 * ⚠️ O botão marcado reflete a DECISÃO dele se houver uma, senão o CHUTE —
 * mas só a decisão é gravada. Gravar o chute junto transformaria um palpite
 * em escolha do dono: no dia em que a lista de sementes melhorasse, as
 * categorias que ele nunca tocou continuariam presas ao chute velho.
 *
 * ⚠️ Categoria que o chute não reconhece aparece com os TRÊS botões apagados
 * — é o convite visual para ele decidir. Marcar um por padrão esconderia
 * exatamente o que precisa de atenção.
 */
export function TiposDeGasto() {
  const { t } = useTranslation();
  const { data: categorias } = useCategories();
  const [prefBruta, setPref] = useSyncedPref(CHAVE_PREF);
  const [mostrarLista, setMostrarLista] = useState(false);

  /**
   * ⚠️⚠️ FIX ROUND 1 (Critical 1) — a dimensão é tipo de GASTO, não tipo de
   * dinheiro. Categoria de RECEITA (`is_income`) não tem "essencial vs
   * luxo vs investimento" — classificar "Salário" pediria uma decisão que
   * não significa nada. Categoria OCULTA (`hidden`, na própria categoria OU
   * no grupo dela) é uma categoria que o dono já decidiu não usar mais; sem
   * este cruzamento com `categorias.grouped`, uma categoria dentro de um
   * grupo oculto passava batido (o campo `hidden` da categoria só existe
   * nela mesma, não herda do grupo — `getCategories()` no loot-core não
   * filtra nada disso, devolve tudo).
   */
  const gruposOcultos = new Set(
    (categorias?.grouped ?? [])
      .filter(grupo => grupo.hidden)
      .map(grupo => grupo.id),
  );
  const lista = (categorias?.list ?? []).filter(
    categoria =>
      !categoria.is_income &&
      !categoria.hidden &&
      !gruposOcultos.has(categoria.group),
  );

  /**
   * ⚠️⚠️ FIX ROUND 1 (Important 2) — `setPref` (`useSyncedPref`) é
   * ASSÍNCRONO: despacha `saveSyncedPrefs`, que faz um round-trip real antes
   * de `prefBruta` (vindo do Redux) atualizar. Classificar categorias é
   * exatamente uma tarefa de clique rápido, várias seguidas — sem esta
   * dança, dois cliques antes do primeiro round-trip voltar fechariam sobre
   * o MESMO mapa obsoleto: cada `escolher()` gravaria `{...mapaVelho,
   * suaMudança}`, e quem responder por último apagaria a outra em silêncio.
   *
   * `mapaRef` é o mapa "em voo": todo clique novo parte DELE (não do `mapa`
   * do render), então cliques acumulam mesmo antes do primeiro round-trip
   * terminar. `mapa` (o `useState`) é só o que o render usa pra pintar a
   * tela na hora, sem esperar o servidor confirmar (otimista).
   *
   * ⚠️ NÃO simplifique isto pra `lerMapa(prefBruta)` direto no corpo do
   * componente — é exatamente essa simplificação, que parece redundante à
   * primeira vista, que reintroduz o bug dos dois cliques.
   *
   * ⚠️ Isto NÃO cobre uma escrita que falhe por rede: fica uma string órfã em
   * `ecosPendentes` (abaixo) e, se um dia uma mudança externa chegasse com
   * EXATAMENTE aquela string, seria ignorada — inofensivo, porque a tela já
   * estaria mostrando aquele estado.
   */
  const [mapa, setMapa] = useState<MapaDeTipos>(() => lerMapa(prefBruta));
  const mapaRef = useRef(mapa);

  /**
   * As strings que ESTE componente mandou gravar e cujo eco ainda não voltou.
   *
   * ⚠️⚠️ FIX ROUND 3 — é um CONJUNTO, não um slot único, e isso importa. A
   * versão anterior (`ultimoEnviado`, um slot só) guardava só a ÚLTIMA
   * escrita: duas escolhas rápidas (A, depois B) cujos ecos voltassem FORA
   * DE ORDEM faziam a tela REGREDIR para A — o eco de A não casava com o
   * slot (que já guardava B) e era lido como mudança externa — e depois
   * FICAR PRESA em A, porque o eco de B casava e era ignorado. O servidor
   * tinha B salvo e a tela mostrava A, sem erro nenhum, até o dono reclicar
   * ou recarregar. Com o conjunto, cada eco que chega é tirado dele; A E B
   * são reconhecidos como ecos próprios, na ordem que chegarem.
   *
   * ⚠️ Também substitui um contador de escritas pendentes, tentado no round
   * 1 e revertido: `useSelector` devolve uma string e faz bail-out quando o
   * valor não muda, então uma escrita BYTE-IDÊNTICA à anterior — por
   * exemplo clicar num tipo que já está ativo — não causava re-render, o
   * efeito abaixo nunca rodava, e o contador ficava travado positivo PRA
   * SEMPRE, engolindo em silêncio toda mudança vinda de outro dispositivo
   * dali em diante. O conjunto não tem esse estado pra travar: uma escrita
   * que falhe deixa uma string que simplesmente nunca casa, e mudanças
   * externas seguintes continuam sendo aplicadas normalmente.
   */
  const ecosPendentes = useRef(new Set<string>());

  useEffect(() => {
    // O eco de uma escrita nossa: a tela já mostra esse estado (ou um mais
    // novo, se outro clique já tiver acontecido). `prefBruta` pode ser
    // `undefined` (pref nunca gravada) — nunca casa com o conjunto, porque
    // `gravarMapa` sempre grava uma string real.
    if (prefBruta !== undefined && ecosPendentes.current.has(prefBruta)) {
      ecosPendentes.current.delete(prefBruta);
      return;
    }

    // Qualquer outra coisa veio de fora (outro dispositivo, ou a carga
    // inicial do servidor).
    const doServidor = lerMapa(prefBruta);
    mapaRef.current = doServidor;
    setMapa(doServidor);
  }, [prefBruta]);

  function escolher(categoriaId: string, tipo: TipoDeGasto) {
    // Clicar no tipo que já está ativo não muda nada — não vale uma escrita
    // (e evita encher `ecosPendentes` à toa).
    if (mapaRef.current[categoriaId] === tipo) return;

    const novo = { ...mapaRef.current, [categoriaId]: tipo };
    mapaRef.current = novo;
    setMapa(novo);

    const serializado = gravarMapa(novo);
    ecosPendentes.current.add(serializado);
    setPref(serializado);
  }

  /**
   * Quantas categorias ainda caem no balde "não classificado" (nem decisão
   * nem chute) — o mesmo balde que a tela Médias mostra sempre, mesmo
   * zerado. É o número que convida o dono a abrir a lista.
   *
   * ⚠️ FIX ROUND 2 — confirmado: é "nem decisão nem chute", não "sem decisão
   * sozinha". Uma categoria que o CHUTE já acertou não entra neste número,
   * mesmo que o dono nunca tenha confirmado nada — a definição é a mesma da
   * tela Médias, que é o que precisa da atenção dele.
   */
  const semTipo = lista.filter(
    categoria => !(mapa[categoria.id] ?? chutarTipo(categoria.name)),
  ).length;

  /**
   * ⚠️⚠️ Revisão final da branch (Finding 7, Minor) — `categorias` vem de
   * `useCategories()` e é `undefined` enquanto a consulta ainda não
   * resolveu. `lista` (acima) já se defende disso (`categorias?.list ?? []`
   * devolve `[]`), mas isso faz `semTipo === 0` ENQUANTO CARREGA — e o
   * cabeçalho então afirma "tudo classificado" antes de saber de verdade.
   * Transitório na prática (a consulta do Actual é rápida e cacheada), mas
   * vira PERMANENTE se a consulta falhar — mesma classe de bug do Finding 1
   * (número plausível e errado, sem aviso). O cabeçalho não pode afirmar
   * nada até `categorias` deixar de ser `undefined`.
   */
  const categoriasCarregando = categorias === undefined;

  return (
    <View style={{ ...cartao, gap: 12 }} data-testid="tipos-de-gasto">
      <Button
        variant="bare"
        data-testid="tipos-de-gasto-alternar"
        aria-expanded={mostrarLista}
        style={{ width: '100%', justifyContent: 'space-between', padding: 0 }}
        onPress={() => setMostrarLista(atual => !atual)}
      >
        <Text style={texto.rotuloCartao}>
          <Trans>Tipos de gasto</Trans>
        </Text>
        <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
          {categoriasCarregando
            ? t('carregando…')
            : semTipo === 0
              ? t('tudo classificado')
              : semTipo === 1
                ? t('1 categoria {{rotulo}}', {
                    rotulo: rotuloSemTipoMinusculo,
                  })
                : t('{{n}} categorias {{rotulo}}', {
                    n: semTipo,
                    rotulo: rotuloSemTipoMinusculo,
                  })}
        </Text>
      </Button>

      {mostrarLista && (
        <>
          <Text style={{ ...texto.corpo, color: cor.tintaFraca }}>
            {t(
              'Já deixei um palpite para cada categoria. Corrija o que discordar — é isso que separa investimento de luxo nas suas médias.',
            )}
          </Text>

          {lista.map(categoria => {
            const decidido = mapa[categoria.id];
            const marcado = decidido ?? chutarTipo(categoria.name);

            return (
              <View
                key={categoria.id}
                style={{
                  flexDirection: 'row',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  gap: 12,
                  paddingTop: 8,
                  paddingBottom: 8,
                }}
              >
                <Text style={texto.corpo}>{categoria.name}</Text>

                <View style={{ flexDirection: 'row', gap: 6 }}>
                  {TIPOS.map(tipo => {
                    const ativo = marcado === tipo;
                    const rotulo = t(ROTULOS[tipo]);
                    return (
                      <Button
                        key={tipo}
                        variant="bare"
                        onPress={() => escolher(categoria.id, tipo)}
                        aria-pressed={ativo}
                        aria-label={`${categoria.name}: ${rotulo}`}
                        data-testid={`tipo-${categoria.id}-${tipo}`}
                        style={{
                          ...texto.rotuloTabela,
                          backgroundColor: ativo ? cor.acento : cor.neutroFraco,
                          color: ativo ? '#ffffff' : cor.tintaFraca,
                          borderRadius: espaco.raioPequeno,
                          padding: '4px 12px',
                        }}
                      >
                        {rotulo}
                      </Button>
                    );
                  })}
                </View>
              </View>
            );
          })}
        </>
      )}
    </View>
  );
}
